import React, { useState } from 'react';

import { Button, Divider, Form, FormItemProps, Select, Tag, message } from 'antd';
import { t } from 'i18next';

import { useGetUsersListQuery, useQuickCreateUserMutation } from '../store/usersApi';

interface Props {
  formProps?: FormItemProps;
  oneSelectMode?: boolean;
  allowQuickCreate?: boolean;
}

export function SelectUsers({ formProps, oneSelectMode, allowQuickCreate = false }: Props) {
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<{ id: number; name: string }[]>([]);
  const form = Form.useFormInstance();
  const { data: { items: users } = { items: [] }, isFetching } = useGetUsersListQuery({ name: search, role: 'user' });
  const [quickCreate, { isLoading: isCreating }] = useQuickCreateUserMutation();
  const name = search.trim().replace(/\s+/g, ' ');
  const matchesExisting = users.some((user) => user.name.trim().toLowerCase() === name.toLowerCase());
  const additionalUsers = selectedUsers.filter((user) => !users.some((item) => item.id === user.id));
  const options = [
    ...users.filter((user) => user.id !== 100000).map((user) => ({
      label: `${user.name}  (ID: ${user.id}, ${user.balance}₽)`,
      value: user.id,
      searchText: user.name,
    })),
    ...additionalUsers.map((user) => ({ label: user.name, value: user.id, searchText: user.name })),
  ];

  const handleQuickCreate = async () => {
    try {
      const result = await quickCreate(name).unwrap();
      setSelectedUsers((current) => [...current, { id: result.data.id, name }]);
      form.setFieldValue('userId', result.data.id);
      setSearch('');
    } catch {
      message.error(t('admin-extensions.quick-create-error'));
    }
  };

  return (
    <Form.Item label={t(`booked-court.titles.${oneSelectMode ? 'user-name' : 'user-names'}`)} name={oneSelectMode ? 'userId' : 'usersId'} {...formProps}>
      <Select
        optionFilterProp="searchText"
        showSearch
        searchValue={search}
        loading={isFetching || isCreating}
        onSearch={setSearch}
        onSelect={(value: number, option: { searchText: string }) => {
          setSelectedUsers((current) => current.some((user) => user.id === value)
            ? current
            : [...current, { id: value, name: option.searchText }]);
          setSearch('');
        }}
        {...(oneSelectMode ? {} : { mode: 'multiple' })}
        filterSort={(optionA, optionB) =>
          (optionA?.searchText ?? '').toLowerCase().localeCompare((optionB?.searchText ?? '').toLowerCase())}
        options={options}
        dropdownRender={(menu) => (
          <>
            {menu}
            {allowQuickCreate && oneSelectMode && name && (
              <>
                <Divider style={{ margin: '4px 0' }} />
                <Button
                  type="text"
                  block
                  loading={isCreating}
                  disabled={isFetching || matchesExisting || name.split(' ').length < 2}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    void handleQuickCreate();
                  }}
                >
                  {t('admin-extensions.quick-create', { name })}
                </Button>
              </>
            )}
          </>
        )}
        tagRender={({ value, closable, onClose }) => (
          <Tag
            style={{ border: '1px solid #d9d9d9', borderRadius: '4px', marginRight: '5px', backgroundColor: '#f0f0f0' }}
          >
            {options.find((user) => user.value === value)?.searchText}
            {closable && (
              <span onClick={onClose} style={{ marginLeft: '5px', fontSize: '16px', cursor: 'pointer' }}>
                &times;
              </span>
            )}
          </Tag>
        )}
      />
    </Form.Item>
  );
}
